import * as Sentry from '@sentry/react'
import axios, { AxiosError } from 'axios'
import { useEffect } from 'react'
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom'

import { AccountInformations } from './account'
import { SENTRY_URL } from './config'
import { getErrorFlashMessagesFn } from './flashUtils'
import { getUserAccountNameFn } from './userUtils'

const isAbortedRequestError = (error: Error): boolean => {
  if (axios.isCancel(error)) return true
  const name = error?.name
  if (name === 'AbortError' || name === 'CanceledError') return true
  const code = (error as AxiosError)?.code
  if (code === 'ERR_CANCELED') return true
  return false
}

export const initSentryFn = () => {
  if (!SENTRY_URL) {
    return
  }
  Sentry.init({
    dsn: SENTRY_URL,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: 1.0,
    beforeSend: (event, hint) => {
      const error = hint?.originalException as Error
      // Filter out cancelled/aborted requests (axios cancel, fetch AbortController, etc.)
      if (error && isAbortedRequestError(error)) {
        return null
      }
      // Not send errors from chrome extension
      if ((error as Error)?.stack?.includes('chrome-extension')) {
        return null
      }
      if (
        (error as AxiosError)?.response?.status === 403 ||
        (error as AxiosError)?.response?.status === 401
      ) {
        return null
      }
      // 400 with a flash message (e.g. unknown dimension): already a warning in the API's Sentry
      if (
        (error as AxiosError)?.response?.status === 400 &&
        getErrorFlashMessagesFn(error).length
      ) {
        return null
      }
      return event
    },
  })
}

export const setSentryUserFn = (user: AccountInformations | null) => {
  if (!SENTRY_URL || !user) {
    return
  }
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: getUserAccountNameFn(user),
  })
  if (user.default_company) {
    Sentry.setTag('client', user.default_company.name)
  }
}

export const setExtraSentryFn = (key: string, value: string | null) => {
  if (!SENTRY_URL) {
    return
  }
  Sentry.setExtra(key, value)
}

export const setSentryTagFn = (key: string, value: string | null) => {
  if (!SENTRY_URL) {
    return
  }
  Sentry.setTag(key, value)
}

export const sendSentryErrorFn = (error: Error) => {
  if (!SENTRY_URL) {
    return
  }
  Sentry.captureException(error)
}
